import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, from, map, mergeMap, of, throwError } from 'rxjs';
import { ExtendedPropertyApiDetail } from 'src/app/module/property/interface/property.interface';
import {
  API_ACTION,
  API_SCOPE,
  API_WRITE_ACTION_TYPE,
  ApiAction,
  ApiScope,
  ApiWriteActionType,
} from 'src/app/shared/interceptor/interceptor.token';
import { isReadOnlyBlockedError } from 'src/app/shared/interceptor/read-only.interceptor';
import { AutomationApiResponse } from 'src/app/shared/interface/billing.interface';
import {
  CommonApiResponse,
  OrderPermission,
  UserPermittedActionsResponse,
} from 'src/app/shared/interface/common.interface';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { environment as ENV } from 'src/environments/environment';
import {
  UnitAdjustmentRecommendationApiRequest,
  UnitAdjustmentRecommendationApiResponse,
} from '../../adjustment-grid/interface/adjustment.interface';
import { AuthService } from '../../auth/service/auth.service';
import { ScenarioState } from '../../scenario/state/scenario.state';
import { SyncUpdateSet } from '../../sync/interface/sync.interface';
import {
  CertificationResponse,
  PreparerDetailsResponse,
  SignOrderStatus,
  UpdatePreparerDetailsRequest,
} from '../interface/certification.interface';
import { DwellingCostApiResponse, DwellingRequest } from '../interface/cost-approach.interface';
import { OrderFeedback, OrderFeedbackData } from '../interface/feedback.interface';
import {
  NeighborhoodAnalysisApiRequest,
  NeighborhoodAnalysisApiResponse,
  NeighborhoodInsight,
} from '../interface/neighborhood.interface';
import { OrderPageName, OrderPageView } from 'src/app/module/order-page-layout/interface/order-page-layout.interface';
import {
  AddCustomCompRes,
  AllStateCountyFips,
  GeocodeApiResponse,
  GetComparablePropertyApiResponse,
  GetSelectedComparablePropertyApiResponse,
  OrderDetail,
  OrderDetailApiResponse,
  OrderLockAPIResponse,
  PrimarySearchFilter,
  ReportSubmitResponse,
  ValidateComparableExistsAPIRes,
  ValidateComparableExistsRequest,
  ValuationType,
} from '../interface/order.interface';
import { PreviewDocumentApiRequest } from '../interface/preview.interface';
import { SimilarityScoreAttributes } from '../interface/similarity-score.interface';
import { OrderState } from '../state/order.state';

@Injectable({
  providedIn: 'root',
})
export class OrderApi {
  constructor(
    private _http: HttpClient,
    private scenarioState: ScenarioState,
    private snackBarService: SnackBarService,
    public orderState: OrderState,
    private authService: AuthService
  ) {}

  /** Get details of an order by order id */
  getOrderDetail(
    orderId?: string | null,
    referenceId?: string | null,
    action?: string | null
  ): Observable<OrderDetail> {
    const httpParams: any = {
      ...(orderId ? { id: orderId } : {}),
      ...(referenceId ? { referenceId } : {}),
      ...(action ? { action: action } : {}),
    };

    return this._http
      .get<CommonApiResponse<OrderDetailApiResponse>>(`${ENV.backendBaseUrl}/api/orders/getOrderById`, {
        params: httpParams,
        context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.ORDER),
      })
      .pipe(
        map((res) => res.data.order),
        catchError((error: HttpErrorResponse) => {
          const errorMessage = error.error.message;
          this.snackBarService.open('error', errorMessage);
          return throwError(() => error);
        })
      );
  }

  /** Initiate comparable generation based on the criteria provided */
  searchComparables(
    orderId: string,
    scenarioId: string,
    params: PrimarySearchFilter | null | undefined
  ): Observable<unknown> {
    const httpParams: any = {
      id: orderId,
      scenarioId,
      ...params,
    };
    return this._http
      .post<CommonApiResponse<unknown>>(`${ENV.backendBaseUrl}/api/searchComparables`, httpParams, {
        context: new HttpContext().set(API_ACTION, ApiAction.WRITE).set(API_SCOPE, ApiScope.ORDER),
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          const errorMessage = error.error.message;
          this.snackBarService.open('error', errorMessage);
          return throwError(() => error);
        })
      );
  }

  /** Get comparables based on order id and applied filters */
  getComparables(params: any): Observable<GetComparablePropertyApiResponse> {
    return this._http
      .post<CommonApiResponse<GetComparablePropertyApiResponse>>(`${ENV.backendBaseUrl}/api/getComparables`, params, {
        context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.ORDER),
      })
      .pipe(map((res) => res.data));
  }

  /** Get only selected comparables based on order id */
  getSelectedComparables(
    orderId: string,
    scenarioId: string,
    valuationType: ValuationType,
    sortBy?: string
  ): Observable<ExtendedPropertyApiDetail[]> {
    const httpParams: any = { id: orderId, scenarioId, valuationType };
    if (sortBy) httpParams.sortBy = sortBy;
    return this._http
      .get<CommonApiResponse<GetSelectedComparablePropertyApiResponse>>(
        `${ENV.backendBaseUrl}/api/getSelectedComparables`,
        {
          params: httpParams,
          context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.ORDER),
        }
      )
      .pipe(map((res) => res.data.selectedComparables));
  }

  /** Initiate similarity score recomputation */
  computeSimilarityScore(
    orderId: string,
    scenarioId: string,
    similarityScores: SimilarityScoreAttributes
  ): Observable<unknown> {
    return this._http
      .post<CommonApiResponse<unknown>>(
        `${ENV.backendBaseUrl}/api/computeSimilarityScore?id=${orderId}&scenarioId=${scenarioId}`,
        similarityScores,
        {
          context: new HttpContext().set(API_ACTION, ApiAction.WRITE).set(API_SCOPE, ApiScope.ORDER),
        }
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          const errorMessage = error.error.message;
          this.snackBarService.open('error', errorMessage);
          return throwError(() => error);
        })
      );
  }

  downloadComparables(params: any): Observable<void> {
    const queryParams = new URLSearchParams();
    queryParams.set('id', params.id);
    queryParams.set('scenarioId', params.scenarioId);
    const fileType = params.type === 'pdf' ? 'application/pdf' : 'text/csv';
    queryParams.set('type', fileType);
    queryParams.set('valuationType', this.orderState.activeValuationType as string);

    const apiUrlWithParams = `${ENV.backendBaseUrl}/api/compsExportService?${queryParams.toString()}`;
    return this._http
      .post(apiUrlWithParams, null, {
        responseType: 'blob',
        context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.ORDER),
      })
      .pipe(
        map((res: Blob) => {
          const blob = new Blob([res], { type: fileType });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `comparable-properties.${params.type}`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          return;
        }),
        catchError(({ error }: HttpErrorResponse) => {
          if (error instanceof Blob) {
            return from(error.text()).pipe(
              mergeMap((text) => {
                try {
                  const commonError = JSON.parse(text) as CommonApiResponse;
                  const errorMessage = commonError.message;
                  this.snackBarService.open('error', errorMessage);
                  return throwError(() => commonError);
                } catch {
                  this.snackBarService.open('error', 'An unknown error occurred');
                  return throwError(() => new Error('Invalid JSON response'));
                }
              })
            );
          } else {
            this.snackBarService.open('error', 'An unknown error occurred');
            return throwError(() => new Error('Unexpected error type'));
          }
        })
      );
  }

  /**Get geocode information */
  getGeocode(params: any): Observable<GeocodeApiResponse> {
    const httpParams: any = {
      api: '/v1/apiGeoCode',
      authType: 'static',
      apiType: 'geocode',
      ...params,
    };
    return this._http
      .get<
        CommonApiResponse<GeocodeApiResponse>
      >(`${ENV.backendBaseUrl}/api/proxy/get`, { params: httpParams, context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.ORDER) })
      .pipe(
        map((res) => res.data),
        catchError((error: HttpErrorResponse) => {
          const errorMessage = error.error.message;
          this.snackBarService.open('error', errorMessage);
          return throwError(() => error);
        })
      );
  }

  /** Add comparable */
  addComparable(params: any): Observable<AddCustomCompRes> {
    const httpParams: any = {
      ...params,
    };
    return this._http
      .post<CommonApiResponse<AddCustomCompRes>>(`${ENV.backendBaseUrl}/api/addComparables`, httpParams, {
        context: new HttpContext().set(API_ACTION, ApiAction.WRITE).set(API_SCOPE, ApiScope.ORDER),
      })
      .pipe(
        map((res) => res.data),
        catchError((error: HttpErrorResponse) => {
          const errorMessage = error.error.message;
          this.snackBarService.open('error', errorMessage);
          return throwError(() => error);
        })
      );
  }

  /** Remove comparable */
  removeComparable(propertyId: string, valuationType: ValuationType): Observable<any> {
    return this._http
      .delete<CommonApiResponse<any>>(`${ENV.backendBaseUrl}/api/removeComparable/${propertyId}`, {
        params: { valuationType },
        context: new HttpContext().set(API_ACTION, ApiAction.WRITE).set(API_SCOPE, ApiScope.ORDER),
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          const errorMessage = error.error.message;
          this.snackBarService.open('error', errorMessage);
          return throwError(() => error);
        })
      );
  }

  /** Add custom comparable */
  addCustomComparable(params: any): Observable<AddCustomCompRes> {
    const httpParams: any = {
      ...params,
    };
    return this._http
      .post<CommonApiResponse<AddCustomCompRes>>(`${ENV.backendBaseUrl}/api/addNewCustomComparables`, httpParams, {
        context: new HttpContext().set(API_ACTION, ApiAction.WRITE).set(API_SCOPE, ApiScope.ORDER),
      })
      .pipe(
        map((res) => res.data),
        catchError((error: HttpErrorResponse) => {
          this.snackBarService.open('error', (error?.error?.message as string) || 'Failed to add custom comparable.');
          return throwError(() => error);
        })
      );
  }

  retrieveDwellingCostData(params: DwellingRequest): Observable<DwellingCostApiResponse> {
    return this._http
      .post<CommonApiResponse<DwellingCostApiResponse>>(`${ENV.backendBaseUrl}/api/getDwellingCostData`, params, {
        context: new HttpContext().set(API_ACTION, ApiAction.WRITE).set(API_SCOPE, ApiScope.ORDER),
      })
      .pipe(
        map((res) => res.data),
        catchError((error: HttpErrorResponse) => {
          const errorMessage = error.error.message;
          this.snackBarService.open('error', errorMessage);
          return throwError(() => error);
        })
      );
  }

  /** Updating order */
  updateOrder(orderId: string, reportType: string, params: SyncUpdateSet[]): Observable<boolean> {
    return this._http
      .patch<CommonApiResponse<SyncUpdateSet>>(
        `${ENV.backendBaseUrl}/api/updateOrder/${orderId}?reportType=${reportType}`,
        params,
        {
          context: new HttpContext().set(API_ACTION, ApiAction.WRITE).set(API_SCOPE, ApiScope.ORDER),
        }
      )
      .pipe(
        map(() => true),
        catchError((error: HttpErrorResponse) => {
          if (isReadOnlyBlockedError(error)) return of(false);

          const errorMessage = error.error.message;
          this.snackBarService.open('error', errorMessage);
          return of(false);
        })
      );
  }

  getUnitAdjustmentRecommendation(
    params?: UnitAdjustmentRecommendationApiRequest
  ): Observable<UnitAdjustmentRecommendationApiResponse | null> {
    return this._http
      .post<CommonApiResponse<UnitAdjustmentRecommendationApiResponse>>(
        `${ENV.backendBaseUrl}/api/calculateAdjustmentModel`,
        params,
        {
          context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.ORDER),
        }
      )
      .pipe(
        map((res) => res.data),
        catchError(() => of(null))
      );
  }

  /** Get Neighborhood Analysis Data */
  getNeighborhoodAnalysis(params: NeighborhoodAnalysisApiRequest): Observable<NeighborhoodAnalysisApiResponse | null> {
    return this._http
      .post<CommonApiResponse<NeighborhoodAnalysisApiResponse>>(
        `${ENV.backendBaseUrl}/api/order/getInventoryAnalysis`,
        params,
        {
          context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.ORDER),
        }
      )
      .pipe(
        map((res) => res.data),
        catchError(() => of(null))
      );
  }

  /** Get a config-driven order page layout. Resolves 400 (no layout configured) to null. */
  getPageLayout(pageName: OrderPageName, reportType: string): Observable<OrderPageView | null> {
    return this._http
      .get<CommonApiResponse<{ pageLayout: OrderPageView }>>(`${ENV.backendBaseUrl}/api/getPageLayoutDetails`, {
        params: { reportType, pageName },
        context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.COMPANY),
      })
      .pipe(
        map((res) => res.data?.pageLayout ?? null),
        catchError(() => of(null))
      );
  }

  /** Get Neighborhood Insights for charts */
  getNeighborhoodInsight(orderId: string, refreshData: boolean = false): Observable<NeighborhoodInsight> {
    return this._http
      .get<CommonApiResponse<NeighborhoodInsight>>(`${ENV.backendBaseUrl}/api/insights`, {
        params: {
          id: orderId,
          scenarioId: this.scenarioState.activeScenario!.id,
          refreshData,
        },
        context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.ORDER),
      })
      .pipe(
        map((res) => res.data),
        catchError((error: HttpErrorResponse) => {
          const errorMessage = error.error.message;
          this.snackBarService.open('error', errorMessage);
          return throwError(() => error);
        })
      );
  }

  getInspectionRecord(id: string): Observable<any> {
    const params = new HttpParams().set('id', id);
    return this._http
      .post<CommonApiResponse>(`${ENV.backendBaseUrl}/api/order/getInspectionRecord`, null, {
        params,
        context: new HttpContext().set(API_ACTION, ApiAction.WRITE).set(API_SCOPE, ApiScope.ORDER),
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          const errorMessage = error.error.message;
          this.snackBarService.open('error', errorMessage);
          return throwError(() => error);
        })
      );
  }

  getAccessControl(orderObjectId: string): Observable<OrderPermission[]> {
    const httpParams: any = {
      scope: 'order',
      orderObjectId,
    };
    return this._http
      .get<CommonApiResponse<UserPermittedActionsResponse>>(`${ENV.backendBaseUrl}/api/accessControl`, {
        params: httpParams,
        context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.ORDER),
      })
      .pipe(
        map((res) => res.data.permittedActions as OrderPermission[]),
        catchError((error: HttpErrorResponse) => {
          const errorMessage = error.error.message;
          this.snackBarService.open('error', errorMessage);
          return throwError(() => error);
        })
      );
  }

  updateInspectionDetails(inspectionUpdateRequest: any): Observable<any> {
    return this._http.post<CommonApiResponse>(
      `${ENV.backendBaseUrl}/api/order/updateInspectionDetails`,
      inspectionUpdateRequest,
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
        context: new HttpContext().set(API_ACTION, ApiAction.WRITE).set(API_SCOPE, ApiScope.ORDER),
      }
    );
  }

  getLicenseDetails(orderId: string, refreshData: boolean = false): Observable<CertificationResponse> {
    return this._http
      .get<CommonApiResponse<CertificationResponse>>(`${ENV.backendBaseUrl}/api/order/getLicenseDetails`, {
        params: { orderId: orderId, refreshData },
        context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.ORDER),
      })
      .pipe(
        map((res) => res.data),
        catchError((error: HttpErrorResponse) => {
          const errorMessage = error.error.message;
          this.snackBarService.open('error', errorMessage);
          return throwError(() => error);
        })
      );
  }

  signOrder(orderId: string): Observable<CommonApiResponse<SignOrderStatus>> {
    const signedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this._http.post<CommonApiResponse<SignOrderStatus>>(
      `${ENV.backendBaseUrl}/api/order/signOrder`,
      {
        orderId,
        signedTimeZone,
      },
      {
        context: new HttpContext().set(API_ACTION, ApiAction.WRITE).set(API_SCOPE, ApiScope.ORDER),
      }
    );
  }

  updatePreparerDetails(body: UpdatePreparerDetailsRequest): Observable<CommonApiResponse<PreparerDetailsResponse>> {
    return this._http.post<CommonApiResponse<PreparerDetailsResponse>>(
      `${ENV.backendBaseUrl}/api/order/updatePreparerDetails`,
      body,
      {
        context: new HttpContext().set(API_ACTION, ApiAction.WRITE).set(API_SCOPE, ApiScope.ORDER),
      }
    );
  }

  /**Get preview file
   * @param id Order Id
   * @param documentType Type of the document to return
   */
  getPreviewDocument(params: PreviewDocumentApiRequest): Observable<any> {
    const httpParams: any = { ...params };
    return this._http
      .get(`${ENV.backendBaseUrl}/api/exportHTML`, {
        params: httpParams,
        responseType: 'blob',
        context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.ORDER),
      })
      .pipe(
        catchError(({ error }: HttpErrorResponse) => {
          if (error instanceof Blob) {
            return from(error.text()).pipe(
              mergeMap((text) => {
                try {
                  const commonError = JSON.parse(text) as CommonApiResponse;
                  const errorMessage = commonError.message;
                  this.snackBarService.open('error', errorMessage);
                  return throwError(() => commonError);
                } catch {
                  this.snackBarService.open('error', 'An unknown error occurred');
                  return throwError(() => new Error('Invalid JSON response'));
                }
              })
            );
          } else {
            this.snackBarService.open('error', 'An unknown error occurred');
            return throwError(() => new Error('Unexpected error type'));
          }
        })
      );
  }

  getReportDocument(params: PreviewDocumentApiRequest): Observable<HttpResponse<Blob> | false> {
    let downloadEndpoint = '';
    switch (params.documentType) {
      case 'PDF':
        downloadEndpoint = 'api/exportPDF';
        break;
      case 'MISMO':
        downloadEndpoint = 'api/exportMISMO';
        break;
      case 'UAD36':
        downloadEndpoint = 'api/exportUAD';
        break;
      case 'ALAMODE':
        downloadEndpoint = 'api/exportAlamode';
        break;
      case 'AIREADY_PDF':
        downloadEndpoint = 'api/exportAIReadyPDF';
        break;
      case 'ENV':
        downloadEndpoint = 'api/exportENV';
        break;
      default:
        downloadEndpoint = 'api/exportPDF';
    }
    const httpParams: any = { orderObjectId: params.orderObjectId };
    return this._http
      .get(`${ENV.backendBaseUrl}/${downloadEndpoint}`, {
        params: httpParams,
        responseType: 'blob',
        observe: 'response',
        context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.ORDER),
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          const errorMessage = error.error.message;
          this.snackBarService.open('error', errorMessage);
          return throwError(() => error);
        })
      );
  }

  /**Get All State CountyFips*/
  getAllStateCountyFips(): Observable<AllStateCountyFips> {
    return this._http
      .get<CommonApiResponse<AllStateCountyFips>>(`${ENV.backendBaseUrl}/api/getAllStateCountyFips`, {
        context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.ORDER),
      })
      .pipe(
        map((res) => res.data),
        catchError((error: HttpErrorResponse) => {
          const errorMessage = error.error.message;
          this.snackBarService.open('error', errorMessage);
          return throwError(() => error);
        })
      );
  }

  /**Get MLs Boards */
  getMLSBoards(params: any): Observable<any> {
    const httpParams: any = {
      api: '/mls/v3/GetMLSBoards',
      authType: 'app_static',
      apiType: 'data',
      ...params,
    };

    return this._http.get<CommonApiResponse<any>>(`${ENV.backendBaseUrl}/api/proxy/get`, {
      params: httpParams,
      context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.ORDER),
    });
  }

  /**
   * Checking comparable exists in comparable list
   */
  validateComparableExists(params: ValidateComparableExistsRequest): Observable<ValidateComparableExistsAPIRes | null> {
    return this._http
      .post<CommonApiResponse<ValidateComparableExistsAPIRes>>(
        `${ENV.backendBaseUrl}/api/validateComparableExists`,
        params,
        {
          context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.ORDER),
        }
      )
      .pipe(
        map((res) => res.data),
        catchError((error: HttpErrorResponse) => {
          const errorMessage = error.error.message;
          this.snackBarService.open('error', errorMessage);
          return throwError(() => error);
        })
      );
  }

  runAutomation(orderId: string, scenarioId: string): Observable<AutomationApiResponse> {
    const httpParams: any = {
      orderId,
      scenarioId,
      forceAutomation: false,
    };
    return this._http
      .post<CommonApiResponse<AutomationApiResponse>>(`${ENV.backendBaseUrl}/api/runAutomation`, httpParams, {
        context: new HttpContext().set(API_ACTION, ApiAction.WRITE).set(API_SCOPE, ApiScope.ORDER),
      })
      .pipe(map((res) => res.data));
  }

  getOrderFeedback(orderId: string): Observable<OrderFeedback[]> {
    const params = new HttpParams().set('orderId', orderId);
    return this._http
      .get<CommonApiResponse<OrderFeedbackData>>(`${ENV.backendBaseUrl}/api/getOrderFeedback`, {
        params,
        context: new HttpContext().set(API_ACTION, ApiAction.READ).set(API_SCOPE, ApiScope.ORDER),
      })
      .pipe(map((res) => res.data.feedback));
  }

  reGenerateReport(orderId: string): Observable<any> {
    return this._http.post<CommonApiResponse>(
      `${ENV.backendBaseUrl}/api/runOrder?id=${orderId}`,
      {},
      {
        context: new HttpContext().set(API_ACTION, ApiAction.WRITE).set(API_SCOPE, ApiScope.ORDER),
      }
    );
  }

  submitReport(orderId: string): Observable<ReportSubmitResponse | null> {
    return this._http
      .post<CommonApiResponse<ReportSubmitResponse>>(
        `${ENV.backendBaseUrl}/api/submitReport?id=${orderId}`,
        {},
        {
          context: new HttpContext().set(API_ACTION, ApiAction.WRITE).set(API_SCOPE, ApiScope.ORDER),
        }
      )
      .pipe(
        map((res) => res?.data),
        catchError((error: HttpErrorResponse) => {
          const errorMessage = error.error.message;
          this.snackBarService.open('error', errorMessage);
          return of(null);
        })
      );
  }

  public generateComment(
    orderId: string,
    commentType: string,
    scenarioId?: string,
    propertyId?: string
  ): Observable<string> {
    return this._http
      .post<CommonApiResponse<string>>(
        `${ENV.backendBaseUrl}/api/generateComment`,
        {
          orderId,
          commentType,
          scenarioId,
          propertyId,
        },
        {
          context: new HttpContext().set(API_ACTION, ApiAction.WRITE).set(API_SCOPE, ApiScope.ORDER),
        }
      )
      .pipe(
        map((res) => res.data),
        catchError((error: HttpErrorResponse) => {
          const errorMessage = error.error?.message;
          this.snackBarService.open('error', errorMessage ?? 'Unable to generate comment.');
          return of('');
        })
      );
  }

  acquireLock(orderId: string): Observable<OrderLockAPIResponse | null> {
    return this._http
      .post<CommonApiResponse<OrderLockAPIResponse>>(
        `${ENV.backendBaseUrl}/api/orders/${orderId}/edit-lock/acquire`,
        {},
        {
          context: new HttpContext()
            .set(API_ACTION, ApiAction.WRITE)
            .set(API_SCOPE, ApiScope.ORDER)
            .set(API_WRITE_ACTION_TYPE, ApiWriteActionType.ACTIVATE),
        }
      )
      .pipe(map((res) => res?.data ?? null));
  }

  refreshLock(orderId: string): Observable<OrderLockAPIResponse | null> {
    return this._http
      .post<CommonApiResponse<OrderLockAPIResponse>>(
        `${ENV.backendBaseUrl}/api/orders/${orderId}/edit-lock/heartbeat`,
        {},
        {
          context: new HttpContext()
            .set(API_ACTION, ApiAction.WRITE)
            .set(API_SCOPE, ApiScope.ORDER)
            .set(API_WRITE_ACTION_TYPE, ApiWriteActionType.ACTIVATE),
        }
      )
      .pipe(
        map((res) => res?.data ?? null),
        catchError(() => {
          return of(null);
        })
      );
  }

  releaseLock(orderId: string, lockToken: string | null, keepalive = false): Observable<boolean> | void {
    if (keepalive) {
      this.authService
        .getAuthToken()
        .then((auth) =>
          fetch(`${ENV.backendBaseUrl}/api/orders/${orderId}/edit-lock/release`, {
            method: 'POST',
            keepalive: true,
            headers: {
              Authorization: `Bearer ${auth?.accessToken ?? ''}`,
              'Content-Type': 'application/json',
              'X-Valuation-Order-Lock-Token': lockToken ?? '',
            },
          })
        )
        .catch(() => {});
      return;
    }

    return this._http
      .post<CommonApiResponse<void>>(
        `${ENV.backendBaseUrl}/api/orders/${orderId}/edit-lock/release`,
        {},
        {
          context: new HttpContext()
            .set(API_ACTION, ApiAction.WRITE)
            .set(API_SCOPE, ApiScope.ORDER)
            .set(API_WRITE_ACTION_TYPE, ApiWriteActionType.DEACTIVATE),
          headers: new HttpHeaders({ 'X-Valuation-Order-Lock-Token': lockToken ?? '' }),
        }
      )
      .pipe(
        map(() => true),
        catchError(() => of(false))
      );
  }
}
